import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type {
  BusinessSettings,
  DashboardBusinessesResponse,
  DashboardOverview,
  DashboardWorkspace,
  UpdateBusinessSettingsRequest,
} from "@/api/types";
import { sessionRevision } from "@/lib/session-request-scope";
import { renameDashboardContext } from "@/lib/session-transition";
import { dashboardQueryKeyForSlug, useDashboardRequest } from "./dashboard-request";

type UpdateBusinessSettingsVariables = UpdateBusinessSettingsRequest & {
  previousSlug?: string;
};

export function useBusinessSettings(slugOverride?: string) {
  const dashboard = useDashboardRequest(slugOverride);

  return useQuery({
    queryKey: dashboard.queryKey("settings"),
    queryFn: () =>
      api.get("dashboard/settings", dashboard.requestOptions()).json<BusinessSettings>(),
    staleTime: 60_000,
  });
}

export function useUpdateBusinessSettings() {
  const queryClient = useQueryClient();
  const dashboard = useDashboardRequest();

  return useMutation({
    onMutate: () => sessionRevision(),
    mutationFn: ({ previousSlug: _previousSlug, ...data }: UpdateBusinessSettingsVariables) =>
      api
        .patch("dashboard/settings", { ...dashboard.requestOptions(), json: data })
        .json<BusinessSettings>(),
    onSuccess: async (data, variables, revision) => {
      if (revision !== sessionRevision()) return;
      await queryClient.cancelQueries({
        queryKey: ["dashboard"],
        predicate: (query) => {
          const part = query.queryKey.at(-1);
          return part === "overview" || part === "settings" || part === "businesses";
        },
      });
      if (revision !== sessionRevision()) return;
      const identity = {
        name: data.name,
        slug: data.slug,
        country: data.country,
        currency: data.currency,
        location_city: data.location?.city ?? null,
      };
      const updateWorkspace = (workspace: DashboardWorkspace) =>
        workspace.id === data.id ? { ...workspace, ...identity } : workspace;

      queryClient.setQueriesData<DashboardOverview>(
        {
          queryKey: ["dashboard"],
          predicate: (query) => query.queryKey.at(-1) === "overview",
        },
        (overview) =>
          overview && {
            ...overview,
            business:
              overview.business.id === data.id
                ? {
                    ...overview.business,
                    ...identity,
                    timezone: data.location?.timezone ?? null,
                    business_hours: data.business_hours,
                  }
                : overview.business,
            businesses: overview.businesses.map(updateWorkspace),
          },
      );
      queryClient.setQueriesData<DashboardBusinessesResponse>(
        {
          queryKey: ["dashboard"],
          predicate: (query) => query.queryKey.at(-1) === "businesses",
        },
        (list) => list && { ...list, businesses: list.businesses.map(updateWorkspace) },
      );
      queryClient.setQueryData(dashboard.queryKey("settings"), data);

      const previousSlug = variables.previousSlug ?? dashboard.slug;
      if (previousSlug !== data.slug) {
        renameDashboardContext(queryClient, {
          previousSlug: dashboard.slug ?? "selected",
          slug: data.slug,
          businessId: data.id,
          revision,
        });
        queryClient.setQueryData(dashboardQueryKeyForSlug(data.slug, "settings"), data);
        const overview = queryClient.getQueryData<DashboardOverview>(
          dashboard.queryKey("overview"),
        );
        if (overview) {
          queryClient.setQueryData(dashboardQueryKeyForSlug(data.slug, "overview"), overview);
        }
        const businesses = queryClient.getQueryData<DashboardBusinessesResponse>(
          dashboard.queryKey("businesses"),
        );
        if (businesses) {
          queryClient.setQueryData(dashboardQueryKeyForSlug(data.slug, "businesses"), businesses);
        }
      }
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      void queryClient.invalidateQueries({ queryKey: ["businesses", data.slug], exact: false });

      if (previousSlug && previousSlug !== data.slug) {
        void queryClient.invalidateQueries({
          queryKey: ["businesses", previousSlug],
          exact: false,
        });
      }
    },
  });
}
